import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup
} from '@angular/forms';
import {provideMomentDateAdapter} from '@angular/material-moment-adapter';
import {ActivatedRoute, Router} from '@angular/router';
import {FeedbackService} from "../service/feedback.service";
import {Feedback, Form, FormItem} from "../repository/entity/feedback.entity";
import {Observable, tap} from "rxjs";
import {Section} from "../service/dto/section";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {UploadImageComponent} from "../../../shared/upload-image/upload-image.component";
import {MatDialog} from "@angular/material/dialog";
import {environment} from "../../../../environments/environment";

export const MY_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-feedback',
  templateUrl: './feedback.component.html',
  styleUrl: './feedback.component.scss',
  providers: [
    provideMomentDateAdapter(MY_FORMATS),
  ],
})
export class FeedbackComponent implements OnInit {
  destroyRef = inject(DestroyRef)

  public companyCode: string;
  public refNum: string;
  public feedback: Observable<Feedback>
  public sectionForm: Observable<{ [key: string]: Form[] }>
  public sections: Observable<Section[]>
  activeSectionParam: string;
  feedbackForm: FormGroup;
  imageTmpEndpoint = environment.baseUrl + "/upload/image/tmp/";

  stateOpenRatingForm: string;

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private _feedbackService: FeedbackService,
    private dialog: MatDialog) {
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      // Access the query parameters here
      this.activeSectionParam = params['section'];
      this._feedbackService.navigateSection(this.activeSectionParam)
    });

    const companyCode = this.route.snapshot.paramMap.get('companyCode');
    if (companyCode) {
      this.companyCode = companyCode
    }
    const refNum = this.route.snapshot.paramMap.get('refNum');
    if (refNum) {
      this.refNum = refNum
    }
    if (!companyCode || !refNum) {
      return;
    }
    this._feedbackService.fetchFeedback(companyCode, refNum)
      .pipe(
        tap((feedback) => {
          if (feedback.feedbackId && feedback.feedbackId > 0) {
            this.router.navigate(['./success'], {
              relativeTo: this.route
            })
            return
          } else {
            if (feedback && feedback.hasExpired) {
              this.router.navigate(['./expired'], {
                relativeTo: this.route
              });
              return;
            }
          }
        }),
        tap((feedback) => {
          const formGroup = this._feedbackService.buildFormGroup(feedback)
          this.feedbackForm = this.formBuilder.group(formGroup)
        }),
        tap(() => {
          this.feedback = this._feedbackService.feedback
          this.sectionForm = this._feedbackService.sectionForm
          this.sections = this._feedbackService.sections
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        error: (err) => {
          if (err.status != 200) {
            this.router.navigate(['404'])
            return
          }
        }
      })
  }

  async onNextStep(sectionForm: { [key: string]: Form[] }, section: Section | null) {
    const next = section?.nextSection
    const current = section?.currentSection
    if (!current) return;
    let err: number[] = this.onValidationRequiredForm(sectionForm[current]);
    if (err.length > 0) {
      this.scrollToFirstError();
      return;
    }

    await this._feedbackService.markSectionCompleted(current)
    if (next == null) {
      return;
    }
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
    await this._feedbackService.navigateSection(next)
  }

  getIsSelected(formName: string, itemId: number): boolean {
    const form = this.feedbackForm.get(formName.toString()) as FormArray
    const formLevel2 = form.get("value") as FormArray
    return formLevel2.get(itemId.toString())?.value
  }

  isShowOtherValue(formName: string, items: FormItem[]): boolean {
    const form = this.feedbackForm.get(formName.toString()) as FormArray
    const value = form.get("value")?.value
    let found = items.find((i) => i.isAllowOtherValue && i.value == value)
    return !!found
  }

  onChangeValue(value: any, formName: string) {
    this.stateOpenRatingForm = formName;
    const form = this.feedbackForm.get(formName.toString()) as FormArray
    form.get("value")?.setValue(value)
    form.markAsTouched();
  }

  onChangeOtherValue(notes: string, formName: string) {
    const form = this.feedbackForm.get(formName.toString()) as FormArray
    form.get("otherValue")?.setValue(notes)
  }

  onChangeImages(images: string[], formName: string) {
    const form = this.feedbackForm.get(formName.toString()) as FormArray
    form.get("images")?.setValue(images)
  }

  getInitialValue(formName: string): string {
    const form = this.feedbackForm.get(formName.toString()) as FormArray
    return form.get("value")?.value
  }

  getInitialOtherValue(formName: string): string {
    const form = this.feedbackForm.get(formName.toString()) as FormArray
    return form.get("otherValue")?.value
  }

  getInitialImages(formName: string): string[] {
    const form = this.feedbackForm.get(formName.toString()) as FormArray
    return form.get("images")?.value
  }

  markAllAsTouched() {
    Object.values(this.feedbackForm.controls).forEach(control => {
      control.markAsTouched();
    });
  }

  async onSubmit(sectionForm: { [key: string]: Form[] }, section: Section | null) {
    const current = section?.currentSection
    if (!current) return;
    let err: number[] = this.onValidationRequiredForm(sectionForm[current]);
    if (!this.feedbackForm.valid || err.length > 0) {
      this.feedbackForm.markAsTouched()
      this.markAllAsTouched();
      this.scrollToFirstError();
      return
    }
    try {
      let feedback = await this._feedbackService.submitFeedback(this.companyCode, this.refNum, this.feedbackForm)
      if (feedback.feedbackId && feedback.feedbackId > 0) {
        this.router.navigate(['./success'], {
          relativeTo: this.route
        })
        return
      }
    } catch (e) {
      this.router.navigate(['404'])
      return
    }

  }

  validationError = <T, >(value: T) => {
    const form = this.feedbackForm.get(<string>value);
    const formInvalid = form?.touched && !form?.valid;
    if (['star', 'sales_menu'].includes(form?.value.typeForm)) {
      return formInvalid ||
             form?.value.value > 0
             && form?.value.value < 4
             && !form?.value.otherValue
    }
    if (form?.value.typeForm == 'radio' && ['tidak', 'no'].includes(form?.value.value)) {
      const findItemSelect = form?.value.items.find((i: any) => i.value == form?.value.value);
      return formInvalid ||
             findItemSelect 
             && findItemSelect.isAllowOtherValue
             && !form?.value.otherValue;
    }
    return formInvalid;
  }

  openUploadDialog(formName: string, action: string): void {
    const dialogRef = this.dialog.open(UploadImageComponent, {
      panelClass: "border-radius-dialog",
      disableClose: true,
      autoFocus: false,
      data: {
        companyCode: this.companyCode,
        action: action,
        filename: this.getInitialValue(formName)
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result.imagesPath) {
        this.onChangeValue(result.imagesPath[0], formName)
      }
    });
  }

  onValidationRequiredForm(forms: Form[]) {
    let err: number[] = []
    forms.forEach((form) => {
      if (!this.feedbackForm.get(form.formControlName.toString())?.valid) {
        err.push(form.id)
        this.feedbackForm.get(form.formControlName.toString())?.markAsTouched()
      }
      const fbForm = this.feedbackForm.get(form.formControlName.toString());
      if (['star', 'sales_menu'].includes(fbForm?.value.typeForm)) {
        if (fbForm?.value.value > 0
            && fbForm?.value.value < 4
            && !fbForm?.value.otherValue
        ) {
            err.push(form.id)
            this.feedbackForm.get(form.formControlName.toString())?.markAsTouched()
        }
      }
      if (fbForm?.value.typeForm == 'radio' && ['tidak', 'no'].includes(fbForm?.value.value)) {
        const findItemSelect = fbForm?.value.items.find((i: any) => i.value == fbForm?.value.value);
        if (findItemSelect 
            && findItemSelect.isAllowOtherValue
            && !fbForm?.value.otherValue
        ) {
            err.push(form.id)
            this.feedbackForm.get(form.formControlName.toString())?.markAsTouched()
        }
      }
    });
    return err;
  }

  scrollToFirstError() {
    for (const key of Object.keys(this.feedbackForm.controls)) {
      if (this.feedbackForm.controls[key].invalid) {
        const invalidControl = document.getElementById(key);
        if (invalidControl) {
          const yOffset = -100; // Adjust the offset as needed
          const y = invalidControl.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({top: y, behavior: 'smooth'});
          invalidControl.focus();
          break;
        }
      }
    }
  }
}
